const moment = require("moment");
/**
 * @fileoverview Reservation Format Service
 * 
 * This module provides utility functions for formatting various types of data
 * related to reservations, bookings, and travel services. All functions handle
 * data transformation from internal codes/values to user-friendly display strings.
 * 
 * @module format.service
 * @namespace ReservationFormatters
 * @author RhomTrip Development Team
 */

/**
 * Formats payment type codes into user-friendly display strings
 * 
 * Converts internal payment type identifiers to readable payment method names
 * for display in user interfaces, reports, and documentation.
 * 
 * @function formatPaymentType
 * @param {string} type - The payment type code to format
 * @returns {string} The formatted payment type display name
 */
export function formatPaymentType(type) {
    if (type === "ota") {
        return "OTA";
    } else if (type === "card") {
        return "Credit Card";
    } else if (type === "transfer") {
        return "Bank Transfer";
    } else if (type === "credit") {
        return "Credit";
    }
    return "Other";
}

/**
 * Formats service category codes into user-friendly class descriptions
 * 
 * Converts internal service category identifiers to readable service class names
 * for display in booking interfaces and service descriptions.
 * 
 * @function formatServiceCategory
 * @param {string} type - The service category code to format
 * @returns {string} The formatted service category display name
 */
export function formatServiceCategory(type) {
    if (type === "standard") {
        return "Standard Class";
    } else if (type === "business") {
        return "Business Class";
    } else {
        return "Luxury Class";
    }
}
/**
 * Formats transfer type codes into user-friendly transfer descriptions
 * 
 * Converts internal transfer type identifiers to readable transfer service names
 * for display in booking interfaces and service selection menus.
 * 
 * @function formatTransferType
 * @param {string} type - The transfer type code to format
 * @returns {string|undefined} The formatted transfer type display name, or undefined for unknown types
 */
export function formatTransferType(type) {
    if (type === "airport") {
        return "Airport Transfer";
    } else if (type === "arrival") {
        return "Airport Transfer (Arrival)";
    } else if (type === "departure") {
        return "Airport Transfer (Departure)";
    } else if (type === "station") {
        return "Station Transfer";
    } else if (type === "port") {
        return "Port Transfer";
    } else if (type === "hourly") {
        return "Hourly";
    } else if (type === "journey") {
        return "Journey";
    }
}

/**
 * Formats client type codes into user-friendly client category descriptions
 * 
 * Converts internal client type identifiers to readable client category names
 * for display in customer management interfaces and reporting systems.
 * 
 * @function formatClientType
 * @param {string} type - The client type code to format
 * @returns {string} The formatted client type display name
 */
export function formatClientType(type) {
    if (type === "customer") {
        return "Customer";
    } else if (type === "travel_agency") {
        return "Travel Agency";
    } else if (type === "business") {
        return "Business";
    } else if (type === "travel_access") {
        return "Agent";
    } else if (type === "guest") {
        return "Guest";
    } else {
        return "Customer";
    }
}

/**
 * Formats passenger type codes into user-friendly passenger category descriptions
 *
 * Converts internal passenger type identifiers to readable passenger category names
 * for display in booking interfaces and service selection menus.
 *
 * @function formatPassengerType
 * @param {string} type - The passenger type code to format
 * @returns {string} The formatted passenger type display name
 */
export function formatPassengerType(type) {
    if (type === "ADULT") {
        return "Adult";
    } else if (type === "SENIOR") {
        return "Senior";
    } else {
        return "Child";
    }
}

/** * Formats position type codes into user-friendly position descriptions
 *
 * Converts internal position type identifiers to readable position names
 * for display in employee management interfaces and reporting systems.
 *
 * @function formatPosition
 * @param {string} type - The position type code to format
 * @returns {string} The formatted position display name
 */
export function formatPosition(type) {
    if (type === "administration-finance") {
        return " Administration & Finance";
    } else if (type === "co-founder-cto") {
        return "Co-Founder & CTO";
    } else if (type === "co-founder-coo") {
        return "Co-Founder & COO";
    } else if (type === "co-founder-ceo") {
        return "Co-Founder & CEO";
    } else if (type === "customer-service-representative") {
        return "CSR";
    } else if (type === "data-entry-operator") {
        return "Data Entry Operator";
    } else if (type === "designer-front-end") {
        return "Designer & Front End";
    } else if (type === "developer") {
        return "Developer";
    } else if (type === "junior-reservations-sales-agent") {
        return "Junior Reservations & Sales Agent";
    } else if (type === "product-manager") {
        return "Product Manager";
    } else if (type === "reservations-csr-manager") {
        return "Reservations & CSR Manager";
    } else if (type === "reservations-sales-agent") {
        return "Reservations & Sales Agent";
    } else if (type === "sales-manager") {
        return "Sales Manager";
    } else if (type === "account-manager") {
        return "Account Manager";
    } else if (type == "customer-service-manager") {
        return "Customer Service Manager";
    } else if (type === "customer-service-claims-manager") {
        return "Customer Service & Claims Manager";
    }
    return "Unknown";
}

/** * Formats transport type codes into user-friendly transport label descriptions
 *
 * Converts internal transport type identifiers to readable labels for display
 * in booking interfaces and service selection menus.
 *
 * @function formatTransportTypeLabel
 * @param {string} type - The transport type code to format
 * @returns {string} The formatted transport type label
 */
export function formatTransportTypeLabel(type) {
    if (["airport", "arrival", "departure"].includes(type)) {
        return "Flight N°:";
    } else if (type === "station") {
        return "Rail Line:";
    } else if (type === "port") {
        return "Cruise Line:";
    } else if (type === "hourly") {
        return "Hours:";
    }
    return "";
}

/** * Formats booking type codes into user-friendly booking type descriptions
 *
 * Converts internal booking type identifiers to readable booking type names
 * for display in booking interfaces and service selection menus.
 *
 * @function formatBookingTypeLabel
 * @param {string} type - The booking type code to format
 * @returns {string} The formatted booking type label
 */
export function formatBookingTypeLabel(type) {
    if (type === "transfer") {
        return "Base Transfer";
    } else if (type === "experience") {
        return "Base Experience";
    }
    return "Base Service";
}

/** * Formats a price amount to two decimal places as a string.
 * @param {number} amount - The price amount to format.
 * @returns {string} - The formatted price amount as a string.
 */
export function formatPriceAmount(amount) {
    return amount.toFixed(2);
}

/**
 * Format a booking date to "DD-MMM-YYYY" format
 * @param {string} date - The date string to format
 * @returns {string} - The formatted date string
 */
export function formatBookingDate(date) {
    if (date) {
        return moment.parseZone(date).format("DD-MMM-YYYY");
    }
    return "";
}

/** Format a booking time to "hh:mm A" format
 * @param {string} time - The time string to format
 * @returns {string} - The formatted time string
 */
export function formatBookingTime(time) {
    return moment(time, ["hh:mm A"]).format("hh:mm A");
}